import { useCallback, useRef, useState, type UIEvent } from 'react';

/** Scrolled no further than this from the top, the page counts as unscrolled: the footer stays hidden. */
export const FOOTER_REVEAL_OFFSET_PX = 16;

/**
 * Whether the reveal-on-scroll footer shows after `<main>` scrolled from `previousTop` to `top`.
 * Scrolling down past the offset reveals it; ANY scroll up hides it (2026-09-30 user direction: "removed
 * with the first scroll up at any given time"), and so does coming back to the top. A scroll event that
 * did not move the page keeps the current state.
 */
export function footerVisibleAfterScroll(previousTop: number, top: number, wasVisible: boolean): boolean {
  if (top <= FOOTER_REVEAL_OFFSET_PX) return false;
  if (top > previousTop) return true;
  if (top < previousTop) return false;
  return wasVisible;
}

/**
 * The footer's visibility and the `onScroll` handler of the element that scrolls the page (AppShell's
 * `<main>`). The handler remembers the last scroll position, so each event knows which way the page moved.
 */
export function useFooterReveal() {
  const [visible, setVisible] = useState(false);
  const lastTop = useRef(0);
  const onScroll = useCallback((event: UIEvent<HTMLElement>) => {
    const top = event.currentTarget.scrollTop;
    const previousTop = lastTop.current;
    lastTop.current = top;
    setVisible(wasVisible => footerVisibleAfterScroll(previousTop, top, wasVisible));
  }, []);
  return { visible, onScroll };
}
